import React, { useState, useEffect } from "react";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3002";

const PLAN_ICONS  = { "Starter": "🌱", "Professional": "🚀", "Business": "⚡", "Unlimited": "♾️" };
const PLAN_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b"];

function formatINR(v) {
  return "₹" + parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

export default function PremiumPlans({ username, currentSubscription, onSubscribed, onClose }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/plans`)
      .then(r => r.json())
      .then(d => setPlans(d.plans || []));
  }, []);

  const checkCoupon = async () => {
    if (!couponCode.trim() || !selectedPlan) {
      setCouponResult({ valid: false, error: "Enter a coupon code and select a plan first." });
      return;
    }
    setCouponChecking(true);
    setCouponResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/coupons/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponCode.trim(), plan_id: selectedPlan.id, username }),
      });
      const data = await res.json();
      setCouponResult(data);
    } catch (err) {
      setCouponResult({ valid: false, error: "Network error." });
    }
    setCouponChecking(false);
  };

  const getDiscountedPrice = () => {
    if (!selectedPlan) return 0;
    if (!couponResult?.valid) return parseFloat(selectedPlan.price);
    return parseFloat(selectedPlan.price) * (1 - couponResult.discount_pct / 100);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) { setStatus({ type: "error", msg: "Please select a plan." }); return; }
    setSubscribing(true); setStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, plan_id: selectedPlan.id, coupon_code: couponResult?.valid ? couponCode : null }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: "success", msg: `🎉 Subscribed to ${data.plan}! ${data.discount_pct > 0 ? `${data.discount_pct}% discount applied.` : ""}` });
        setTimeout(() => onSubscribed && onSubscribed(data), 1500);
      } else {
        setStatus({ type: "error", msg: data.error || "Subscription failed." });
      }
    } catch (err) {
      setStatus({ type: "error", msg: err.message });
    }
    setSubscribing(false);
  };

  const s = {
    overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
    modal:     { background: "#0f1117", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, width: "100%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto", padding: 32, color: "#e2e8f0", fontFamily: "'Inter', sans-serif" },
    planCard:  (sel, color) => ({ flex: "1 1 180px", background: sel ? `${color}15` : "rgba(255,255,255,0.04)", border: `2px solid ${sel ? color : "rgba(255,255,255,0.08)"}`, borderRadius: 16, padding: 20, cursor: "pointer", transition: "all 0.2s", position: "relative", textAlign: "center" }),
    input:     { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, padding: "11px 14px", color: "#e2e8f0", fontSize: 14, outline: "none", flex: 1 },
    btn:       (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "opacity 0.15s" }),
    tag:       (color) => ({ background: `${color}22`, border: `1px solid ${color}44`, color, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, display: "inline-block" }),
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>Choose Your Plan</div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Upgrade to unlock more conversations with MuriAI</div>
            {currentSubscription && (
              <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 8, padding: "4px 10px", fontSize: 12 }}>
                <span style={{ color: "#4ade80" }}>✅ Current: </span>
                <span style={{ color: "#e2e8f0", fontWeight: 600 }}>{currentSubscription.plan_name}</span>
                <span style={{ color: "#64748b" }}>· {currentSubscription.chats_used}/{currentSubscription.chat_limit === -1 ? "∞" : currentSubscription.chat_limit} chats used</span>
              </div>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#94a3b8", cursor: "pointer", padding: "6px 14px", fontSize: 13 }}>✕ Close</button>
          )}
        </div>

        {/* Plans Grid */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
          {plans.map((plan, i) => {
            const color = PLAN_COLORS[i % PLAN_COLORS.length];
            const icon = PLAN_ICONS[plan.name] || "💎";
            const sel = selectedPlan?.id === plan.id;
            const isUnlimited = plan.chat_limit === -1;
            return (
              <div key={plan.id} style={s.planCard(sel, color)} onClick={() => { setSelectedPlan(plan); setCouponResult(null); setCouponCode(""); setStatus(null); }}>
                {sel && <div style={{ position: "absolute", top: 10, right: 10, width: 20, height: 20, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>✓</div>}
                <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color, marginBottom: 4 }}>{formatINR(plan.price)}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>per month</div>
                <div style={{ ...s.tag(color), marginBottom: 8 }}>
                  {isUnlimited ? "♾️ Unlimited chats" : `💬 ${plan.chat_limit} chats/month`}
                </div>
                {plan.description && (
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 8, lineHeight: 1.5 }}>{plan.description}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Coupon Section */}
        {selectedPlan && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 18, marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              🏷️ Have a coupon code?
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <input
                style={s.input}
                placeholder="Enter coupon code (e.g. SAVE20)"
                value={couponCode}
                onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                onKeyDown={e => e.key === "Enter" && checkCoupon()}
              />
              <button style={{ ...s.btn("#6366f1"), padding: "11px 20px" }} onClick={checkCoupon} disabled={couponChecking}>
                {couponChecking ? "…" : "Apply"}
              </button>
            </div>
            {couponResult && (
              <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 8, fontSize: 12,
                background: couponResult.valid ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${couponResult.valid ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                color: couponResult.valid ? "#4ade80" : "#f87171" }}>
                {couponResult.valid
                  ? `🎉 ${couponResult.discount_pct}% discount applied! You save ${formatINR(parseFloat(selectedPlan.price) * couponResult.discount_pct / 100)}`
                  : `❌ ${couponResult.error}`}
              </div>
            )}
          </div>
        )}

        {/* Price Summary + Subscribe */}
        {selectedPlan && (
          <div style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 14, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
              <div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 4 }}>Order Summary</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedPlan.name} Plan</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                  {selectedPlan.chat_limit === -1 ? "Unlimited chats" : `${selectedPlan.chat_limit} chats per month`}
                </div>
                {couponResult?.valid && (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, textDecoration: "line-through" }}>
                    Original: {formatINR(selectedPlan.price)}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: "#a5b4fc" }}>{formatINR(getDiscountedPrice())}</div>
                {couponResult?.valid && (
                  <div style={{ fontSize: 12, color: "#4ade80" }}>🎉 {couponResult.discount_pct}% off with {couponCode}</div>
                )}
              </div>
            </div>
            <button
              style={{ ...s.btn("#6366f1"), width: "100%", justifyContent: "center", marginTop: 16, padding: 14, fontSize: 15, borderRadius: 12, opacity: subscribing ? 0.7 : 1 }}
              onClick={handleSubscribe}
              disabled={subscribing}>
              {subscribing ? "Processing…" : `🚀 Subscribe Now — ${formatINR(getDiscountedPrice())}/month`}
            </button>
            {status && (
              <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                background: status.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${status.type === "success" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                color: status.type === "success" ? "#4ade80" : "#f87171", textAlign: "center" }}>
                {status.msg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}